<template>
  <div class="time-of-day-condition">
    <div class="field">
      <div class="control">
        <n-radio-buttons
          v-model="scheduleType"
          label="Business Hours"
          :options="timeOfDayOptions"
          :hideLabel="true"
          @update:model-value="handleSelectionChange"
        />
      </div>
    </div>

    <div class="time-of-day-condition__online-times-picker" v-if="scheduleType === 2">
      <BusinessHoursSelection
        v-model="customTimes"
        :allowEmpty="false"
        @error="customScheduleInvalid"
        @input="customScheduleChanged"
      />
    </div>
    <div class="time-of-day-condition__control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BusinessHoursSelection from '@/features/bots/components/businessHoursSelection/BusinessHoursSelection.vue';
import { ConditionViewModel } from '../models/conditionViewModel';
import { BusinessHoursViewModel } from '@/features/bots/models';
import { BusinessHoursOptions } from '@/features/bots/components/businessHoursSelection';
import { PropType, ref, onMounted } from 'vue';

enum ScheduleType {
  InHours,
  OutOfHours,
  CustomTimes,
}

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const customTimes = ref<BusinessHoursViewModel[]>([]);
const scheduleType = ref<ScheduleType>(ScheduleType.InHours);

const timeOfDayOptions = [
  { value: ScheduleType.InHours, label: 'In hours' },
  { value: ScheduleType.OutOfHours, label: 'Out of hours' },
  { value: ScheduleType.CustomTimes, label: 'Custom' },
];

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

onMounted(() => {
  liveCondition.value = props.condition;
  setConditionType();
  handleSelectionChange(scheduleType.value);
});

function handleSelectionChange(type: ScheduleType): void {
  scheduleType.value = type;
  if (type === ScheduleType.InHours) {
    selectInHours();
  } else if (type === ScheduleType.OutOfHours) {
    selectInHours(false);
  } else if (type === ScheduleType.CustomTimes) {
    selectCustomSchedule();
  }
  emit('changed', liveCondition.value);
}

function customScheduleChanged() {
  liveCondition.value.result = true;
  liveCondition.value.value = JSON.stringify(customTimes.value);
  emit('changed', liveCondition.value);
}

function customScheduleInvalid(invalid: boolean) {
  liveCondition.value.invalid = invalid;
}

function complete() {
  emit('completed', liveCondition.value);
}

function selectInHours(isInHours: boolean = true) {
  liveCondition.value = {
    attribute: props.condition.attribute,
    operation: 'in',
    type: props.condition.type,
    value: 'bot.openingTimes',
    result: isInHours,
    unit: props.condition.unit,
    invalid: false,
  };
}

function setConditionType() {
  if (!liveCondition.value.value) {
    return;
  }
  if (liveCondition.value.value === 'bot.openingTimes') {
    scheduleType.value = liveCondition.value.result ? ScheduleType.InHours : ScheduleType.OutOfHours;
  } else {
    scheduleType.value = ScheduleType.CustomTimes;
  }
}

function selectCustomSchedule() {
  liveCondition.value.result = true;
  try {
    customTimes.value = JSON.parse(liveCondition.value.value);
  } catch {
    customTimes.value = [BusinessHoursOptions.getDefaultModel()];
    liveCondition.value.value = JSON.stringify([BusinessHoursOptions.getDefaultModel()]);
  }
}
</script>

<style lang="scss">
.time-of-day-condition {
  &__online-times-picker {
    width: 540px;
  }

  &__control {
    margin-top: 10px;
  }
}
</style>
